import { toast } from "sonner";
import {
  isEncryptionReady,
  isSensitiveKey,
  encryptValue,
  decryptValue,
  isEncrypted,
} from "@/lib/cryptoStorage";

/**
 * Safely parse JSON from localStorage with error handling, optional
 * validation, and transparent decryption for sensitive health keys.
 *
 * NOTE: For encrypted sensitive data, this returns `defaultValue` since
 * decryption is async. Use `safeParseJSONAsync` instead when reading
 * sensitive keys in async contexts (useEffect callbacks, etc.).
 */
export const safeParseJSON = <T>(
  key: string,
  defaultValue: T,
  validator?: (data: unknown) => data is T
): T => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return defaultValue;

    // If encrypted but encryption not ready, return default
    if (isEncrypted(item) && !isEncryptionReady()) {
      return defaultValue;
    }

    // Encrypted data requires the async variant
    if (isEncrypted(item)) {
      return defaultValue;
    }

    const parsed = JSON.parse(item);
    if (validator && !validator(parsed)) {
      console.warn(`Invalid data format for ${key}, using defaults`);
      localStorage.removeItem(key);
      return defaultValue;
    }
    return parsed;
  } catch (error) {
    console.error(`Failed to parse ${key}:`, error);
    localStorage.removeItem(key);
    if (!sessionStorage.getItem(`notified_${key}`)) {
      toast.error(`Recovered from corrupted ${key} data`);
      sessionStorage.setItem(`notified_${key}`, "true");
    }
    return defaultValue;
  }
};

/**
 * Async version that handles decryption transparently.
 * Use for sensitive keys after encryption has been initialised.
 */
export const safeParseJSONAsync = async <T>(
  key: string,
  defaultValue: T,
  validator?: (data: unknown) => data is T
): Promise<T> => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return defaultValue;

    let jsonString = item;

    if (isEncrypted(item)) {
      if (!isEncryptionReady()) return defaultValue;
      jsonString = await decryptValue(item);
    }

    const parsed = JSON.parse(jsonString);
    if (validator && !validator(parsed)) {
      console.warn(`Invalid data format for ${key}, using defaults`);
      localStorage.removeItem(key);
      return defaultValue;
    }
    return parsed;
  } catch (error) {
    console.error(`Failed to parse ${key}:`, error);
    localStorage.removeItem(key);
    if (!sessionStorage.getItem(`notified_${key}`)) {
      toast.error(`Recovered from corrupted ${key} data`);
      sessionStorage.setItem(`notified_${key}`, "true");
    }
    return defaultValue;
  }
};

/**
 * Async-safe version of safeSaveJSON. Awaits encryption before writing
 * to localStorage, eliminating the fire-and-forget race condition.
 *
 * On encryption failure the data is **not** saved at all (no plaintext
 * fallback) and the user is notified via a toast.
 */
export const safeSaveJSONAsync = async (key: string, value: unknown): Promise<boolean> => {
  try {
    const jsonString = JSON.stringify(value);

    if (isSensitiveKey(key) && isEncryptionReady()) {
      try {
        const encrypted = await encryptValue(jsonString);
        localStorage.setItem(key, encrypted);
        return true;
      } catch (err) {
        console.error(`Encryption failed for ${key}:`, err);
        if (!sessionStorage.getItem(`enc_fail_${key}`)) {
          toast.error("Failed to encrypt data — changes were not saved");
          sessionStorage.setItem(`enc_fail_${key}`, "true");
        }
        return false;
      }
    }

    localStorage.setItem(key, jsonString);
    return true;
  } catch (error) {
    console.error(`Failed to save ${key}:`, error);
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      toast.error("Storage is full - some data may not be saved");
    } else {
      toast.error("Failed to save data");
    }
    return false;
  }
};

/**
 * Sync wrapper — delegates to safeSaveJSONAsync for encrypted keys.
 * @deprecated Prefer safeSaveJSONAsync for sensitive keys.
 */
export const safeSaveJSON = (key: string, value: unknown): boolean => {
  if (isSensitiveKey(key) && isEncryptionReady()) {
    // Delegate to async version — callers should migrate to safeSaveJSONAsync
    void safeSaveJSONAsync(key, value);
    return true;
  }
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Failed to save ${key}:`, error);
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      toast.error("Storage is full - some data may not be saved");
    } else {
      toast.error("Failed to save data");
    }
    return false;
  }
};

/** Type guard for array validation */
export const isArray = (data: unknown): data is unknown[] => Array.isArray(data);

/** Type guard for object validation */
export const isObject = (data: unknown): data is Record<string, unknown> =>
  typeof data === "object" && data !== null && !Array.isArray(data);

/** Type guard for string array validation */
export const isStringArray = (data: unknown): data is string[] =>
  Array.isArray(data) && data.every((item) => typeof item === "string");
